import React, { Component } from "react";
import { ProposalConfirmList } from "./proposalConfirmList";
import { post } from "aws-amplify/api";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import config from "../../config";
import "./proposalForm.css";
import { v4 as uuidv4 } from "uuid";

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      return {
        Authorization: `Bearer ${session.tokens.idToken.toString()}`,
      };
    }
    return {};
  } catch (error) {
    console.log("Error getting auth headers:", error);
    return {};
  }
}

export class ProposalConfirm extends Component {
  _isMounted = false;
  session = undefined;
  excelPage = "";
  pageSize = "";
  excelName = "";
  paperworkFactSheet = "";

  state = {
    sessionReady: false,
    errorStatus: false,
    files: [],
    uploadedFiles: [],
    filesSent: false,
    buttonDisable: false,
    uploadProgress: 0,
    isSubmitting: false,
    submissionId: null,
  };

  file = {
    data: null,
    progress: null,
  };

  addFiles = (allFiles) => {
    this.setState({ files: this.state.files.concat(allFiles) });
  };

  componentDidMount = async () => {
    // Skip all initialization in view-only mode
    if (this.props.viewOnly) {
      this.setState({ sessionReady: true });
      return;
    }

    try {
      this.session = await getCurrentUser().catch((e) => {
        console.log("User not authenticated");
        return null;
      });
      if (this.props.lists && this.props.lists.proposalTypes) {
        this.props.lists.proposalTypes.forEach((element) => {
          if (element.name === this.props.values.program) {
            this.excelName = element.embeddedExcelKeys;
            this.excelPage = element.embeddedExcelPages;
            this.pageSize = element.pageSize;
            this.paperworkFactSheet = element.paperwork.factSheet;
          }
        });
      }
      this.setState({ sessionReady: true });
      if (
        !this.props.values.logoSize &&
        this.props.lists &&
        this.props.lists.advisors
      ) {
        let advisorName = this.props.values.advisorName
          ? this.props.values.advisorName
          : this.props.values.customTemplate;
        for (let advisor of this.props.lists.advisors) {
          if (advisor.Name === advisorName) {
            this.props.handles.handleDefault("logoSize", advisor.LogoSize);
            this.props.handles.handleDefault("logoOffset", advisor.LogoOffset);
            break;
          }
        }
      }
    } catch {
      alert("Error: User not authenticated, please login again!");
    }
  };

  componentWillUnmount = () => {
    this._isMounted = false;
  };

  getProgramPowerPoint = async (programName) => {
    let result = programName;
    if (this.props.lists && this.props.lists.proposalTypes) {
      this.props.lists.proposalTypes.forEach((element) => {
        if (element.name === programName) {
          result = element.powerPoint;
        }
      });
    }
    return result;
  };

  // API data upload functions
  async postData() {
    const { values } = this.props;
    this.logToFile("postData - values object:", values);
    this.logToFile("postData - values.registrations:", values.registrations);
    const programResult = await this.getProgramPowerPoint(values.program);

    var options = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      currency: "USD",
      style: "currency",
      currencyDisplay: "symbol",
    };

    let uuid = uuidv4();

    const apiName = "vc-proposal-generator";
    const path = "/proposal-generator";
    const authHeaders = await getAuthHeaders();
    const Init = {
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      body: {
        // OPTIONAL
        "user-name": values.userName,
        program: values.program,
        customTemplate: values.customTemplate,
        papercheck: values.papercheck,
        othercheck: values.othercheck,
        ProgramActual: programResult,
        "date-time": values.dateTime
          ? new Date(values.dateTime).toUTCString()
          : new Date().toUTCString(),
        CreationTime: Math.floor(+new Date() / 1000),
        "form-info": [
          {
            key: "CLIENT NAMES",
            value: values.clientName,
            type: "string",
          },
          {
            key: "ADVISOR NAME",
            value: values.advisorName
              ? values.advisorName
              : values.customTemplate,
            type: "string",
          },
          {
            key: "REGISTRATION",
            value:
              values.registrations && values.registrations.length > 1
                ? JSON.stringify(values.registrations)
                : values.registrations && values.registrations[0]
                ? values.registrations[0].value || values.registrations[0]
                : "",
            type: "string",
          },
          {
            key: "RISK",
            value:
              values.riskTolerance !== "" &&
              /\d/.test(String(values.riskTolerance))
                ? String(values.riskTolerance).split(" ")[0]
                : values.riskTolerance === ""
                ? ""
                : String(values.riskTolerance),
            type: "string",
          },
          {
            key: "BOND",
            value: values.optionsTM || "Taxable",
            type: "string",
          },
          {
            key: "VALUE",
            value: String(
              Number(
                String(values.assetValue).replace(/[^0-9.-]+/g, "")
              ).toLocaleString(undefined, options)
            ),
            type: "string",
          },
          {
            key: "INCOME",
            value: String(
              Number(String(values.incomeNeeds).replace(/[^0-9.-]+/g, "")) !== 0
                ? Number(
                    String(values.incomeNeeds).replace(/[^0-9.-]+/g, "")
                  ).toLocaleString(undefined, options)
                : ""
            ),
            type: "string",
          },
          {
            key: "LOGO",
            value: values.logo
              ? String(values.logo)
              : values.advisorName
              ? values.advisorName
              : values.customTemplate,
            type: values.logo ? "image" : "string",
          },
          {
            key: "ACCOUNT NUMBERS",
            value:
              values.fusionAccountNumbers &&
              values.fusionAccountNumbers.length > 0
                ? values.fusionAccountNumbers.join(", ")
                : "",
            type: "string",
          },
          // Risk Image only for non-fusion proposals (fusion handles within sections)
          ...(values.program.toLowerCase().includes("fusion")
            ? []
            : [
                {
                  key: "RISK TOLERANCE",
                  value: values.riskTolerance,
                  type: "string",
                },
                {
                  key: "RISK IMAGE",
                  value: JSON.stringify({
                    riskTolerance: values.riskTolerance || "",
                    powerPoint: programResult || "",
                    logo: values.logo || "",
                    optionsTM: values.optionsTM || "",
                    factSheet: this.paperworkFactSheet || [],
                    uuid: uuid,
                    scale: 90,
                  }),
                  type: "image",
                  center: true,
                },
                ...(values.program.toLowerCase().includes("elements")
                  ? []
                  : [
                      {
                        key: "insert-pdf",
                        value: JSON.stringify({
                          optionsTM: values.optionsTM || "",
                          riskTolerance: values.riskTolerance || "",
                          uuid: uuid,
                          powerPoint: programResult || "",
                          factSheet: this.paperworkFactSheet || [],
                        }),
                        type: values.insertType || "Fact Sheet",
                      },
                    ]),
              ]),
          // Include fusion sections if provided as prop
          ...(values.program.toLowerCase().includes("fusion") &&
          this.props.fusionSections &&
          this.props.fusionSections.data &&
          this.props.fusionSections.data.sections
            ? this.props.fusionSections.data.sections
            : []),
        ],
        logoSize: values.logoSize,
        logoOffset: values.logoOffset,
        assetValue: values.assetValue,
        incomeNeeds: values.incomeNeeds,
        optionsTM: values.optionsTM,
        registrations: values.registrations,
        attachments: this.state.files ? this.state.files.join(" ") : "N/A",
      },
    };

    try {
      return await post({ apiName, path, options: Init }).response;
    } catch (e) {
      this.logToFile("API POST failed:", e);
      this.setState({ errorStatus: true });
      throw e;
    }
  }

  async postFiles() {
    const { files } = this.state;
    let errorLocalStatus = false;
    for (const [index, file] of Array.from(files).entries()) {
      if (file && file.size > config.MAX_ATTACHMENT_SIZE) {
        errorLocalStatus = true;
        this.setState({ errorStatus: true });
        let newFiles = [...files];
        newFiles[index] = new File([file], "(FAILED) - " + file.name, {
          type: file.type,
        });
        this.setState({ files: newFiles });
      }
    }
    if (!errorLocalStatus) {
      for (const file of Array.from(files)) {
        if (file) {
          const fileToBinary = await this.toBinaryString(file);
          if (file.size > config.apiGateway.FILELIMIT) {
            let fileSizeSplitArray = this.chunkSubstr(
              fileToBinary,
              config.apiGateway.FILELIMIT
            );
            for (const [index, fileSizeSplit] of Array.from(
              fileSizeSplitArray
            ).entries()) {
              let last = index + 1 === fileSizeSplitArray.length;
              const apiName = "vc-proposal-generator";
              const path = "/proposal-generator/attachments";
              const authHeaders = await getAuthHeaders();
              let Init = {
                headers: {
                  ...authHeaders,
                  "content-type": file.type,
                  accept: file.type,
                  "file-name": file.name,
                            "multi-part-upload": "true",
                  "multi-part-index": `${index}`,
                  "multi-part-last": `${last}`,
                },
                body: Buffer.from(fileSizeSplit, "base64").toString(
                  "base64"
                ),
              };
              await post({ apiName, path, options: Init }).response;
            }
          } else {
            const apiName = "vc-proposal-generator";
            const path = "/proposal-generator/attachments";
            const authHeaders = await getAuthHeaders();
            let Init = {
              headers: {
                ...authHeaders,
                "content-type": file.type,
                accept: file.type,
                "file-name": file.name,
                        "multi-part-upload": "false",
                "multi-part-index": "0",
                "multi-part-last": "false",
              },
              body: Buffer.from(fileToBinary, "base64").toString("base64"),
            };
            await post({ apiName, path, options: Init }).response;
          }
        }
      }
    }
  }

  chunkSubstr(str, size) {
    const numChunks = Math.ceil(str.length / size);
    const chunks = new Array(numChunks);

    for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
      chunks[i] = str.substr(o, size);
    }

    return chunks;
  }

  toBinaryString = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const arrayBuffer = reader.result;
        const bytes = new Uint8Array(arrayBuffer);
        let binary = "";
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        resolve(binary);
      };
      reader.onerror = (error) => reject(error);
    });

  logToFile = (message, data = null) => {
    const timestamp = new Date().toISOString();

    // Log to console
    if (data) {
      console.log(message, data);
    } else {
      console.log(message);
    }

    // Also save to window for IDE access
    if (!window.proposalLogs) window.proposalLogs = [];
    window.proposalLogs.push({ timestamp, message, data });

    // Try to write to local storage as backup
    try {
      const existingLogs =
        localStorage.getItem("proposalSubmissionLogs") || "[]";
      const logs = JSON.parse(existingLogs);
      logs.push({ timestamp, message, data });
      localStorage.setItem(
        "proposalSubmissionLogs",
        JSON.stringify(logs.slice(-50))
      ); // Keep last 50 entries
    } catch (e) {
      console.warn("Could not save to localStorage:", e);
    }
  };

  continue = async (e) => {
    e.preventDefault();

    // Prevent double submission of the same form
    if (this.state.isSubmitting) {
      this.logToFile("Submission already in progress, ignoring duplicate request");
      return;
    }

    const submissionId = uuidv4();

    // Lock this submission and clear any previous error state
    this.setState({
      sessionReady: false,
      isSubmitting: true,
      errorStatus: false,
      submissionId,
    });

    try {
      // Submit data and files concurrently, await both
      const [dataResult, filesResult] = await Promise.allSettled([
        this.postData(),
        this.postFiles(),
      ]);

      const failed =
        dataResult.status === "rejected" || filesResult.status === "rejected";
      if (failed) {
        this.logToFile("Submission failed", { dataResult, filesResult });
        this.setState({
          errorStatus: true,
          isSubmitting: false,
          sessionReady: true,
        });
      } else {
        this.logToFile("Submission succeeded");
        this.props.nextStep();
      }
    } catch (err) {
      this.logToFile("Unexpected submission error:", err);
      this.setState({
        errorStatus: true,
        isSubmitting: false,
        sessionReady: true,
      });
    }
  };

  back = (e) => {
    e.preventDefault();
    this.props.prevStep();
  };

  // File Upload Functions

  handleDrop = (files) => {
    let fileList = this.state.files;
    for (let index = 0; index < files.length; index++) {
      if (!files[index].name) return;
      fileList.push(files[index].name);
    }
    this.setState({ files: fileList });
  };

  handleUploadFileHistory = (file) => {
    let fileList = this.state.uploadedFiles;
    fileList.push(file);
    this.setState({ uploadedFiles: fileList });
  };

  removeA(arr) {
    var what,
      a = arguments,
      L = a.length,
      ax;
    while (L > 1 && arr.length) {
      what = a[--L];
      while ((ax = arr.indexOf(what)) !== -1) {
        arr.splice(ax, 1);
      }
    }
    return arr;
  }

  onFileRemove = (index) => {
    this.state.files.splice(index, 1);
    this.setState({ files: this.state.files });
    this.setState({ errorStatus: false });
  };

  onUploadFileRemove = (index) => {
    this.state.uploadedFiles.splice(index, 1);
    this.setState({ uploadedFiles: this.state.uploadedFiles });
  };

  onUploadFileClear = () => {
    this.setState({ uploadedFiles: [] });
  };

  render() {
    var options = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      currency: "USD",
      style: "currency",
      currencyDisplay: "symbol",
    };
    const { values } = this.props;
    const { files } = this.state;
    return (
      <div>
        {this.state.errorStatus && (
          <div style={{ border: "1px solid red", padding: "5px" }}>
            <p>
              Looks like an error occurred! Please try again or contact support!
            </p>
            {files.length > 0 && <p>The maximum file size is 60MB</p>}
            <p>Contact Support:</p>
            <br />
            <p>(855) 558-4287</p>
          </div>
        )}
        {!this.props.viewOnly && <h1>Confirm:</h1>}
        <div className="row confirm-grid" style={{ margin: "20px 0" }}>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <ProposalConfirmList
              Title="Username"
              Description={values.userName}
            />
          </div>
          {/* {values.customTemplate && values.advisorName !== "" ? (
            <div className="col-lg-6 col-md-6 col-sm-12">
              <ProposalConfirmList
                Title="Customization"
                Description={values.customTemplate}
              />
            </div>
          ) : null} */}
          <div className="col-lg-6 col-md-6 col-sm-12">
            <ProposalConfirmList
              Title="Advisor Name(s)"
              Description={
                values.advisorName ? values.advisorName : values.customTemplate
              }
            />
          </div>
          {values.logo ? (
            <div className="col-lg-6 col-md-6 col-sm-12">
              <ProposalConfirmList
                Title="Logo"
                Description={String(values.logo)}
              />
            </div>
          ) : null}
          <div className="col-lg-6 col-md-6 col-sm-12">
            <ProposalConfirmList
              Title="Client Name(s)"
              Description={values.clientName}
            />
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <ProposalConfirmList
              Title="Strategy"
              Description={values.program}
            />
          </div>

          {values.incomeNeeds &&
          Number(String(values.incomeNeeds).replace(/[^0-9.-]+/g, "")) > 0 ? (
            <div className="col-lg-6 col-md-6 col-sm-12">
              <ProposalConfirmList
                Title="Income Needs Per Month"
                Description={String(
                  Number(
                    String(values.incomeNeeds).replace(/[^0-9.-]+/g, "")
                  ).toLocaleString(undefined, options)
                )}
              />
            </div>
          ) : null}
          {values.registrations &&
            (typeof values.registrations === "string" ? (
              <div className="col-lg-6 col-md-6 col-sm-12">
                <ProposalConfirmList
                  Title="Registration"
                  Description={values.registrations}
                />
              </div>
            ) : (
              Object.entries(values.registrations).map(
                ([, registration], index, array) => (
                  <div key={index} className="col-lg-6 col-md-6 col-sm-12">
                    <ProposalConfirmList
                      Title={`Registration ${
                        array.length > 1 ? index + 1 : ""
                      }`}
                      Description={registration.value || registration}
                    />
                  </div>
                )
              )
            ))}
          <div className="col-lg-6 col-md-6 col-sm-12">
            <ProposalConfirmList
              Title="Value of Assets"
              Description={String(
                Number(
                  String(values.assetValue).replace(/[^0-9.-]+/g, "")
                ).toLocaleString(undefined, options)
              )}
            />
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <ProposalConfirmList
              Title="Risk Tolerance"
              Description={values.riskTolerance}
            />
          </div>
          {/* Display Account Numbers if they exist */}
          {values.fusionAccountNumbers &&
            values.fusionAccountNumbers.length > 0 && (
              <div className="col-lg-6 col-md-6 col-sm-12">
                <ProposalConfirmList
                  Title="Account Number(s)"
                  Description={values.fusionAccountNumbers.join(", ")}
                />
              </div>
            )}

          {/* Fusion-specific display sections */}
          {values.program.toLowerCase().includes("fusion") &&
            this.props.fusionSummary && (
              <>
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <ProposalConfirmList
                    Title="Fusion Rebalance Type"
                    Description={
                      this.props.fusionSummary?.rebalanceType || "Automatic"
                    }
                  />
                </div>
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <ProposalConfirmList
                    Title="Total Fusion Strategies"
                    Description={`${
                      this.props.fusionProposals
                        ? this.props.fusionProposals.length
                        : this.props.fusionSummary?.totalProposals || 0
                    }`}
                  />
                </div>
                {this.props.fusionProposals &&
                this.props.fusionProposals.length > 0
                  ? this.props.fusionProposals.map((proposal, index) => (
                      <div key={index} className="col-lg-6 col-md-6 col-sm-12">
                        <div className="confirm-card">
                          <div className="card-body">
                            <h6 className="card-title vicus-blue mb-2">
                              {proposal.name}
                            </h6>
                            <div className="fusion-component-details">
                              <div className="detail-item">
                                <strong>Client:</strong>{" "}
                                {proposal.clientName || values.clientName}
                              </div>
                              <div className="detail-item">
                                <strong>Registration:</strong>{" "}
                                {proposal.formData?.registrations
                                  ? typeof proposal.formData.registrations ===
                                    "string"
                                    ? proposal.formData.registrations
                                    : Object.values(
                                        proposal.formData.registrations
                                      )[0]?.value ||
                                      Object.values(
                                        proposal.formData.registrations
                                      )[0]
                                  : "N/A"}
                              </div>
                              <div className="detail-item">
                                <strong>Percentage:</strong>{" "}
                                {(
                                  Math.round(proposal.percentage * 100) / 100
                                ).toFixed(2)}
                                %
                              </div>
                              <div className="detail-item">
                                <strong>Risk:</strong>{" "}
                                {proposal.formData?.riskTolerance || "N/A"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  : this.props.fusionSummary?.proposals &&
                    this.props.fusionSummary.proposals.length > 0
                  ? this.props.fusionSummary.proposals.map(
                      (proposal, index) => (
                        <div
                          key={index}
                          className="col-lg-6 col-md-6 col-sm-12"
                        >
                          <div className="confirm-card">
                            <div className="card-body">
                              <h6 className="card-title vicus-blue mb-2">
                                {proposal}
                              </h6>
                              <div className="fusion-component-details">
                                <div className="detail-item">
                                  <strong>Client:</strong> {values.clientName}
                                </div>
                                <div className="detail-item">
                                  <strong>Registration:</strong> N/A
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )
                  : null}
              </>
            )}
        </div>
        {!this.props.viewOnly && (
          <div style={{ marginTop: "2rem" }}>
            <button
              className="btn btn-primary"
              onClick={this.back}
              style={{
                marginBottom: "10px",
                paddingLeft: "30px",
                paddingRight: "30px",
              }}
            >
              Back
            </button>
            <button
              className="btn btn-success"
              onClick={this.continue}
              style={{
                float: "right",
                marginBottom: "10px",
                paddingLeft: "30px",
                paddingRight: "30px",
              }}
              disabled={
                !this.state.sessionReady ||
                this.state.errorStatus ||
                this.state.isSubmitting
              }
            >
              {this.state.isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>{" "}
                  Submitting...
                </>
              ) : (
                "Confirm & Submit"
              )}
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default ProposalConfirm;
